/**
 * Grid-pathway substation + interconnection CAPEX estimator.
 *
 * Coefficients sourced from:
 *   - DOE Quadrennial Energy Review (QER) transmission infrastructure cost tables
 *   - FERC "2023 Report on Costs Associated With Federal Interconnection Requirements"
 *   - ISO/RTO interconnection study summaries (PJM, MISO, ERCOT, CAISO 2023–2024)
 *   - BLS Producer Price Index — electrical equipment & construction (2020 base → 2025)
 *
 * // R-131-FIN-DECISION-B
 */

export interface SubstationEstimateInput {
  siteCapacityMW: number;
  targetVoltageKv: number;
  existingTieInVoltageKv?: number;
  distanceToTieMiles: number;
  isoRegion: string;
}

export interface SubstationEstimate {
  baseCostUsd: number;
  transformerUsd: number;
  breakerBayUsd: number;
  protectionAndMeteringUsd: number;
  landAcquisitionUsd: number;
  interconnectionTieLineUsd: number;
  contingencyUsd: number;
  totalUsd: number;
  confidenceBand: { low: number; high: number };
  sources: string[];
}

/** BLS ENR Construction Cost Index uplift from 2020 baseline to 2025. */
const BLS_CCI_FACTOR_2025 = 1.28;

/** Voltage-class multipliers for civil/structural base cost (DOE QER Table 4-2). */
const VOLTAGE_BASE_MULTIPLIER: Record<number, number> = {
  69: 0.85,
  115: 0.95,
  138: 1.0,
  230: 1.2,
  345: 1.45,
  500: 1.75,
};

/** Transformer $/MVA by voltage class (DOE QER + FERC interconnection cost survey medians). */
const TRANSFORMER_USD_PER_MVA: Record<number, number> = {
  69: 28_000,
  115: 32_000,
  138: 36_000,
  230: 42_000,
  345: 52_000,
  500: 65_000,
};

/** Breaker bay cost by voltage (FERC 2023 network upgrade cost medians). */
const BREAKER_BAY_USD: Record<number, number> = {
  69: 650_000,
  115: 800_000,
  138: 950_000,
  230: 1_100_000,
  345: 1_400_000,
  500: 1_800_000,
};

/** Tie-line $/mile by voltage (FERC interconnection study line-cost medians). */
const TIE_LINE_USD_PER_MILE: Record<number, number> = {
  69: 350_000,
  115: 500_000,
  138: 650_000,
  230: 850_000,
  345: 1_100_000,
  500: 1_500_000,
};

/** ISO-specific contingency uplift (queue congestion / upgrade complexity). */
const ISO_CONTINGENCY_PCT: Record<string, number> = {
  PJM: 0.18,
  MISO: 0.16,
  ERCOT: 0.14,
  CAISO: 0.20,
  SPP: 0.15,
  NYISO: 0.19,
  ISONE: 0.17,
};

const SOURCES = [
  'DOE Quadrennial Energy Review (QER) — transmission substation cost tables',
  'FERC 2023 Report on Costs Associated With Federal Interconnection Requirements',
  'PJM Interconnection Cost Analysis (2024)',
  'MISO Generator Interconnection Queue Report (2024)',
  'BLS PPI — electrical equipment & construction (2020=100, 2025 uplift)',
];

function nearestVoltageTier(kv: number): number {
  const tiers = [69, 115, 138, 230, 345, 500];
  let best = tiers[0];
  let bestDelta = Math.abs(kv - best);
  for (const tier of tiers) {
    const delta = Math.abs(kv - tier);
    if (delta < bestDelta) {
      best = tier;
      bestDelta = delta;
    }
  }
  return best;
}

function voltageMultiplier(kv: number): number {
  const tier = nearestVoltageTier(kv);
  return VOLTAGE_BASE_MULTIPLIER[tier] ?? 1.0;
}

function transformerUsdPerMva(kv: number): number {
  const tier = nearestVoltageTier(kv);
  return TRANSFORMER_USD_PER_MVA[tier] ?? 40_000;
}

function breakerBayCost(kv: number): number {
  const tier = nearestVoltageTier(kv);
  return BREAKER_BAY_USD[tier] ?? 1_000_000;
}

function tieLineCostPerMile(kv: number): number {
  const tier = nearestVoltageTier(kv);
  return TIE_LINE_USD_PER_MILE[tier] ?? 750_000;
}

function contingencyPct(isoRegion: string): number {
  const key = isoRegion.trim().toUpperCase();
  return ISO_CONTINGENCY_PCT[key] ?? 0.15;
}

function transformerCount(mw: number): number {
  if (mw <= 50) return 1;
  if (mw <= 120) return 2;
  return Math.ceil(mw / 80);
}

function breakerBayCount(mw: number): number {
  if (mw <= 30) return 2;
  if (mw <= 100) return 3;
  if (mw <= 250) return 4;
  return 5;
}

/**
 * Estimate customer-side substation + interconnection tie-line CAPEX.
 * Returns a component breakdown with a low/high confidence band.
 */
export function estimateSubstationCost(input: SubstationEstimateInput): SubstationEstimate {
  const {
    siteCapacityMW,
    targetVoltageKv,
    existingTieInVoltageKv,
    distanceToTieMiles,
    isoRegion,
  } = input;

  if (siteCapacityMW <= 0) {
    throw new RangeError('siteCapacityMW must be positive');
  }
  if (distanceToTieMiles < 0) {
    throw new RangeError('distanceToTieMiles must be non-negative');
  }

  const cci = BLS_CCI_FACTOR_2025;
  const vMult = voltageMultiplier(targetVoltageKv);

  // Civil works, control building, foundations — scales with MW and voltage class.
  const baseCostUsd = siteCapacityMW * 45_000 * vMult * cci;

  // Transformer bank(s): MVA ≈ MW × 1.15 (PF + N-1 margin).
  const mvaPerUnit = (siteCapacityMW * 1.15) / transformerCount(siteCapacityMW);
  const transformerUsd =
    transformerCount(siteCapacityMW) *
    mvaPerUnit *
    transformerUsdPerMva(targetVoltageKv) *
    cci;

  // Step-up required when tie-in voltage is below target.
  const stepUpFactor =
    existingTieInVoltageKv != null && existingTieInVoltageKv < targetVoltageKv ? 1.12 : 1.0;

  const breakerBayUsd = breakerBayCount(siteCapacityMW) * breakerBayCost(targetVoltageKv) * cci;

  const equipmentSubtotal = baseCostUsd + transformerUsd * stepUpFactor + breakerBayUsd;
  // C16 — NO SECOND CCI. Every term in `equipmentSubtotal` is already escalated
  // (`baseCostUsd`, `transformerUsd` and `breakerBayUsd` each multiply by `cci`
  // at construction), so applying the index again to a percentage OF that
  // subtotal escalated this component twice. Protection and metering is a
  // fraction of already-escalated equipment cost, not a fresh base-year price.
  const protectionAndMeteringUsd = equipmentSubtotal * 0.08;

  // Land: ~2 acres for ≤100 MW, scaling up for larger facilities.
  const landAcres = Math.max(2, siteCapacityMW / 40);
  const landAcquisitionUsd = landAcres * 125_000 * cci;

  const interconnectionTieLineUsd =
    distanceToTieMiles * tieLineCostPerMile(targetVoltageKv) * cci;

  const subtotal =
    baseCostUsd +
    transformerUsd * stepUpFactor +
    breakerBayUsd +
    protectionAndMeteringUsd +
    landAcquisitionUsd +
    interconnectionTieLineUsd;

  const contingencyUsd = subtotal * contingencyPct(isoRegion);
  const totalUsd = subtotal + contingencyUsd;

  // Confidence band: FERC 2023 interconnection cost p25–p75 spread (~±35%).
  const confidenceBand = {
    low: Math.round(totalUsd * 0.65),
    high: Math.round(totalUsd * 1.45),
  };

  return {
    baseCostUsd: Math.round(baseCostUsd),
    transformerUsd: Math.round(transformerUsd * stepUpFactor),
    breakerBayUsd: Math.round(breakerBayUsd),
    protectionAndMeteringUsd: Math.round(protectionAndMeteringUsd),
    landAcquisitionUsd: Math.round(landAcquisitionUsd),
    interconnectionTieLineUsd: Math.round(interconnectionTieLineUsd),
    contingencyUsd: Math.round(contingencyUsd),
    totalUsd: Math.round(totalUsd),
    confidenceBand,
    sources: SOURCES,
  };
}
